import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChip, MatChipListbox } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UnsubscribeService } from '@webgate/angular-azure-core';
import { Auditor } from 'src/app/models/auditor';
import { AccessService } from 'src/app/services/access.service';
import { FileService, RequestFile } from 'src/app/services/file.service';

import { FileUploadContainerComponent } from './file-upload-container/file-upload-container.component';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  imports: [MatChipListbox, MatChip, MatProgressSpinnerModule, MatIcon, FileUploadContainerComponent]
})
export class FileUploadComponent implements OnInit {
  private fileService = inject(FileService);
  private accessService = inject(AccessService);
  formBuilder = inject(FormBuilder);
  unsubscribeService = inject(UnsubscribeService);

  readonly fileLinkId = input.required<string>();
  readonly onlyDownload = input(false);
  readonly form = input.required<UntypedFormGroup>();
  files = signal<RequestFile[]>([]);
  uploading = false;
  uploadForm!: FormGroup;

  ngOnInit(): void {
    this.loadFiles();
    this.buildForm();
  }

  buildForm(): void {
    this.uploadForm = this.formBuilder.group({ file: [null, Validators.required] });
  }

  loadFiles(): void {
    const fileLinkId = this.fileLinkId();
    if (fileLinkId) {
      this.fileService.getLinkedFiles(fileLinkId).subscribe({
        next: (files) => {
          this.files.set(files);
        }
      });
    }
  }

  openFile(file: RequestFile) {
    if (file && file.id) {
      this.fileService.getFileById(file.id).subscribe({
        next: (resp) => {
          window.open(resp);
        }
      });
    }
  }

  uploadFile(): void {
    if (this.uploadForm.invalid) return;
    const file: File = this.uploadForm.get('file')?.value.get('attachment') as File;
    const attachmentFormData: FormData = new FormData();
    attachmentFormData.append('files', file, file.name);
    this.saveAttachment(attachmentFormData);
  }

  saveAttachment(attachment: FormData): void {
    this.uploading = true;
    this.fileService
      .uploadFile(attachment, this.fileLinkId())
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe(() => {
        this.loadFiles();
        this.uploading = false;
      });
  }

  uploadDone(file: RequestFile): boolean {
    return file.progress === undefined;
  }

  canDelete() {
    switch (this.form().get('auditor')?.value) {
      case Auditor.GPIA:
        return this.accessService.isGpiaAuditor();
      case Auditor.GPRM:
        return this.accessService.isGprmAuditor();
    }
    return false;
  }

  deleteFile(file: RequestFile) {
    if (file.id) {
      this.fileService.deleteFileById(file.id).subscribe((isDeleted) => {
        if (isDeleted) {
          this.files.set(this.files().filter((f) => file.id !== f.id));
        }
      });
    }
  }

  onFileChange(): void {
    // Implementation of onFileChange method
  }

  onUploadComplete(): void {
    // Implementation of onUploadComplete method
  }
}
